// @optimyl/events — typed publish/subscribe, so a failing component need not know about App Insights or
// the toast store. One subscriber, wired in main.tsx, decides what a reported failure means.
//
//   eventBus.publish("error", { error, context: "PaperReview.codeExchange" });

import type { EventMap, EventName } from "./events";

type Listener<T> = (event: T) => void;

class EventBus {
  private listeners = new Map<EventName, Set<Listener<never>>>();

  /** Returns an unsubscribe function. */
  subscribe<K extends EventName>(event: K, listener: Listener<EventMap[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event)!;
    set.add(listener as Listener<never>);

    return () => set.delete(listener as Listener<never>);
  }

  publish<K extends EventName>(event: K, payload: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) {
      try {
        (listener as Listener<EventMap[K]>)(payload);
      } catch (err) {
        // Never rethrow into the publisher: a broken error-subscriber would otherwise turn every reported
        // failure into a second, worse failure at the original call site. Always logged — a silent
        // subscriber fault means every error in the app stops being reported and nothing says so.
        console.error(`[EventBus] Subscriber error for "${event}":`, err);
      }
    }
  }
}

export const eventBus = new EventBus();
